/**
 * F17.2.8 — Tavola tecnica orizzontale del controsoffitto.
 *
 * Layout 1280×720 (16:9), stile catalogo Knauf/SG analogo a `TechnicalWallDrawing`
 * ma con asse Y invertito: TOP = solaio, BOTTOM = pacchetto a vista.
 *
 * Composizione visiva (dall'alto al basso):
 *   1. Header con titolo dinamico + tipologia
 *   2. Datum SOLAIO (concrete pattern + tratteggio in alto)
 *   3. Camera tecnica con N pendini verticali (solo se suspended + drop > 0)
 *   4. Orditura primaria (rettangoli profilo di taglio, distanziati primarySpacingMm)
 *   5. Orditura secondaria (perpendicolare alla primaria — visualizzata come
 *      strato sotto, solo se hasDoubleStructure)
 *   6. Isolante (above_primary visibile come banda continua sopra orditura)
 *   7. Pannello/lastra a vista (filo basso)
 *   8. Quote a sinistra (verticali) + callout numerati a destra
 *   9. Sidebar dx con legenda blocchi + dimensioni complete
 *  10. Footer con info posa (UNI 11424:2015 + EN 13964 controsoffitti)
 *
 * NON mostra:
 *  - vista frontale (la sezione laterale è autosufficiente per il ceiling)
 *  - pavimento (non c'è nei controsoffitti)
 */
import React, { useMemo } from 'react';
import SVGPatternsDefs from './SVGPatternsDefs';
import {
  buildCeilingSectionModel,
  generateCeilingTitle,
  type CeilingBlock,
  type CeilingSectionModel,
} from './ceilingModel';
import type { LayerV2 } from '../../types';
import type { CeilingDimensionalState } from '../../hooks/useCeilingConfiguratorState';
import { formatNumber } from '@/utils/format';

interface TechnicalCeilingDrawingProps {
  layers: LayerV2[];
  ceiling: CeilingDimensionalState;
  /** Codice sistema generato (opzionale). */
  systemCode?: string | null;
  /** Larghezza ideale stanza in mm (per scala asse X). Default 3000. */
  roomWidthMm?: number;
}

const TechnicalCeilingDrawing: React.FC<TechnicalCeilingDrawingProps> = ({
  layers,
  ceiling,
  systemCode,
  roomWidthMm = 3000,
}) => {
  const model = useMemo(() => buildCeilingSectionModel(layers, ceiling), [layers, ceiling]);
  const title = useMemo(() => generateCeilingTitle(model), [model]);

  // Empty state
  if (model.blocks.length <= 1) {
    return (
      <div className="aspect-video bg-white border rounded-lg flex items-center justify-center text-muted-foreground p-12">
        <div className="text-center">
          <div className="text-sm font-medium">Aggiungi i primi layer</div>
          <div className="text-xs mt-1">La sezione tecnica si genera automaticamente.</div>
        </div>
      </div>
    );
  }

  // ===== LAYOUT viewBox 1280×720 =====
  const VB_W = 1280;
  const VB_H = 720;

  const HEADER_H = 64;
  const FOOTER_H = 60;
  const LEFT_PAD = 80;   // spazio per quote a sinistra
  const SIDEBAR_W = 320;
  const RIGHT_PAD = 24;
  const DRAW_X = LEFT_PAD;
  const DRAW_Y = HEADER_H + 30;
  const DRAW_W = VB_W - LEFT_PAD - SIDEBAR_W - RIGHT_PAD;
  const DRAW_H = VB_H - HEADER_H - FOOTER_H - 60;

  // SCALE
  // - scaleY: profondità totale (drop + struct + insul + boards) → 80% DRAW_H
  // - scaleX: roomWidthMm → 90% DRAW_W
  const totalDepthMm = Math.max(model.totalDepthMm, 50);
  const scaleY = (DRAW_H * 0.80) / totalDepthMm;
  const scaleX = (DRAW_W * 0.90) / Math.max(roomWidthMm, 1000);

  // Posizione disegno (centrato)
  const drawRoomPxW = roomWidthMm * scaleX;
  const drawTotalPxH = totalDepthMm * scaleY;
  const drawStartX = DRAW_X + (DRAW_W - drawRoomPxW) / 2;
  const drawStartY = DRAW_Y + (DRAW_H - drawTotalPxH) * 0.4; // leggermente sopra il centro

  // === Calcolo Y cumulato per ogni blocco (top → bottom) ===
  type DrawBlock = {
    block: CeilingBlock;
    y: number;
    h: number;
    calloutNum: number;
  };
  const drawBlocks: DrawBlock[] = [];
  let cursorY = drawStartY;
  let calloutCounter = 1;
  for (const b of model.blocks) {
    const depth =
      b.kind === 'slab' ? b.thicknessMm :
      b.kind === 'board' ? b.thicknessMm :
      b.kind === 'hanger_space' ? b.depthMm :
      b.kind === 'frame' ? b.depthMm :
      b.kind === 'ceiling_insulation' ? b.depthMm : 0;
    const h = depth * scaleY;
    drawBlocks.push({ block: b, y: cursorY, h, calloutNum: calloutCounter });
    if (b.kind !== 'slab') calloutCounter++; // slab non ha callout
    cursorY += h;
  }
  const drawEndY = cursorY;

  return (
    <div className="bg-white border rounded-lg overflow-hidden shadow-sm" data-section-view="true">
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        className="w-full h-auto technical-ceiling-svg"
        xmlns="http://www.w3.org/2000/svg"
      >
        <SVGPatternsDefs />

        {/* === BACKGROUND === */}
        <rect x={0} y={0} width={VB_W} height={VB_H} fill="#ffffff" />

        {/* === HEADER === */}
        <g>
          <text x={LEFT_PAD - 40} y={32} fontSize={20} fontWeight={700} fill="#0F172A" fontFamily="Inter, system-ui, sans-serif">
            {title}
          </text>
          {systemCode && (
            <text x={LEFT_PAD - 40} y={52} fontSize={11} fill="#64748B" fontFamily="ui-monospace, monospace" letterSpacing="0.05em">
              {systemCode}
            </text>
          )}
          <text
            x={LEFT_PAD - 40}
            y={systemCode ? 66 : 52}
            fontSize={9}
            fill="#94A3B8"
            fontFamily="Inter, system-ui, sans-serif"
            letterSpacing="0.1em"
          >
            {model.isSuspended ? 'CONTROSOFFITTO PENDINATO' : 'CONTROSOFFITTO A CONTATTO'}
            {' · scala 1:10 (indicativa)'}
          </text>
          <line x1={LEFT_PAD - 40} y1={HEADER_H + 16} x2={VB_W - SIDEBAR_W - 16} y2={HEADER_H + 16} stroke="#E2E8F0" strokeWidth={0.5} />
        </g>

        {/* === DISEGNO SEZIONE === */}
        <g filter="url(#soft-shadow)">
          {drawBlocks.map((db, idx) => {
            const { block, y, h } = db;
            const blockX = drawStartX;
            const blockW = drawRoomPxW;

            if (block.kind === 'slab') {
              return <SlabRect key={idx} x={blockX} y={y} w={blockW} h={h} />;
            }
            if (block.kind === 'hanger_space') {
              return (
                <HangerSpaceRect
                  key={idx}
                  x={blockX}
                  y={y}
                  w={blockW}
                  h={h}
                  hangerSpacingPx={block.hangerSpacingPrimaryMm * scaleX}
                />
              );
            }
            if (block.kind === 'frame') {
              return (
                <FrameRect
                  key={idx}
                  x={blockX}
                  y={y}
                  w={blockW}
                  h={h}
                  block={block}
                  spacingPx={block.spacingMm * scaleX}
                />
              );
            }
            if (block.kind === 'ceiling_insulation') {
              return (
                <InsulationRect
                  key={idx}
                  x={blockX}
                  y={y}
                  w={blockW}
                  h={h}
                  fillId={block.fillId}
                />
              );
            }
            if (block.kind === 'board') {
              return <BoardRect key={idx} x={blockX} y={y} w={blockW} h={h} fillId={block.fillId} />;
            }
            return null;
          })}
        </g>

        {/* === QUOTE VERTICALI (sinistra) === */}
        <g>
          {/* Quota TOTALE */}
          <VerticalDimension
            x={drawStartX - 50}
            y1={drawStartY + (drawBlocks[0]?.h ?? 0)} // dopo lo slab
            y2={drawEndY}
            label={`${formatNumber((totalDepthMm), 0)} mm`}
            color="#0F172A"
            strong
          />
          {/* Quote singoli blocchi (escluso slab) */}
          {drawBlocks
            .filter((db) => db.block.kind !== 'slab')
            .map((db, idx) => {
              const depth =
                db.block.kind === 'board' ? db.block.thicknessMm :
                db.block.kind === 'hanger_space' ? db.block.depthMm :
                db.block.kind === 'frame' ? db.block.depthMm :
                db.block.kind === 'ceiling_insulation' ? db.block.depthMm : 0;
              return (
                <VerticalDimension
                  key={idx}
                  x={drawStartX - 20}
                  y1={db.y}
                  y2={db.y + db.h}
                  label={`${depth}`}
                  fontSize={8}
                />
              );
            })}
        </g>

        {/* === CALLOUT NUMERATI (sui blocchi non-slab) === */}
        <g>
          {drawBlocks
            .filter((db) => db.block.kind !== 'slab')
            .map((db) => (
              <Callout key={db.calloutNum} num={db.calloutNum} x={drawStartX + drawRoomPxW + 10} y={db.y + db.h / 2} />
            ))}
        </g>

        {/* === SIDEBAR DX: legenda + dimensioni === */}
        <CeilingSidebar
          x={VB_W - SIDEBAR_W - RIGHT_PAD}
          y={HEADER_H}
          w={SIDEBAR_W}
          h={VB_H - HEADER_H - FOOTER_H}
          drawBlocks={drawBlocks}
          model={model}
          ceiling={ceiling}
        />

        {/* === FOOTER === */}
        <g>
          <line x1={LEFT_PAD - 40} y1={VB_H - FOOTER_H} x2={VB_W - RIGHT_PAD} y2={VB_H - FOOTER_H} stroke="#E2E8F0" strokeWidth={0.5} />
          <text x={LEFT_PAD - 40} y={VB_H - FOOTER_H + 18} fontSize={9} fill="#64748B" fontFamily="Inter, system-ui, sans-serif">
            Posa secondo UNI 11424:2015 + EN 13964 · Pendini ogni {ceiling.hangerSpacingPrimaryMm}×{ceiling.hangerSpacingPerpendicularMm} mm
            · Profilo perimetrale a tutto il perimetro stanza
          </text>
          <text x={LEFT_PAD - 40} y={VB_H - FOOTER_H + 34} fontSize={9} fill="#94A3B8" fontFamily="Inter, system-ui, sans-serif">
            Sezione tecnica generata automaticamente · DryConfig · scala approssimativa, non sostituisce esecutivo
          </text>
        </g>
      </svg>
    </div>
  );
};

// ============================================================================
// Sub-componenti SVG
// ============================================================================

const SlabRect: React.FC<{ x: number; y: number; w: number; h: number }> = ({ x, y, w, h }) => (
  <g>
    <rect x={x} y={y} width={w} height={h} fill="url(#pat-concrete)" stroke="#71717A" strokeWidth={0.6} />
    {/* Tratteggio decorativo sopra il solaio (datum esistente) */}
    <line x1={x} y1={y - 2} x2={x + w} y2={y - 2} stroke="#71717A" strokeWidth={0.5} strokeDasharray="4 3" />
    <text x={x + 6} y={y + h / 2 + 3} fontSize={9} fill="#52525B" fontFamily="Inter, system-ui, sans-serif">
      SOLAIO ESISTENTE
    </text>
  </g>
);

const HangerSpaceRect: React.FC<{ x: number; y: number; w: number; h: number; hangerSpacingPx: number }> = ({
  x, y, w, h, hangerSpacingPx,
}) => {
  // Pendini come linee verticali con anchor in alto (gancio) e piastra in basso
  const hangers: number[] = [];
  if (hangerSpacingPx > 8) {
    // Primo pendino centrato rispetto al primo passo (offset = passo/2)
    const offset = hangerSpacingPx / 2;
    for (let cx = x + offset; cx < x + w; cx += hangerSpacingPx) {
      hangers.push(cx);
    }
  }
  return (
    <g>
      {/* Background camera tecnica con sfondo molto chiaro */}
      <rect x={x} y={y} width={w} height={h} fill="#F8FAFC" stroke="#CBD5E1" strokeWidth={0.4} strokeDasharray="2 2" />
      {/* Etichetta camera tecnica */}
      <text x={x + 6} y={y + 12} fontSize={8} fill="#64748B" fontFamily="Inter, system-ui, sans-serif" letterSpacing="0.05em">
        CAMERA TECNICA
      </text>
      {/* Pendini visibili */}
      {hangers.map((cx, i) => (
        <g key={i}>
          {/* Gancio al solaio (cerchietto in alto) */}
          <circle cx={cx} cy={y + 2} r={2.5} fill="#475569" />
          {/* Asta verticale */}
          <line x1={cx} y1={y + 2} x2={cx} y2={y + h - 2} stroke="#475569" strokeWidth={1.4} />
          {/* Piastra inferiore (ancoraggio orditura) */}
          <rect x={cx - 4} y={y + h - 4} width={8} height={3} fill="#334155" />
        </g>
      ))}
    </g>
  );
};

const FrameRect: React.FC<{
  x: number; y: number; w: number; h: number;
  block: CeilingBlock & { kind: 'frame' };
  spacingPx: number;
}> = ({ x, y, w, h, block, spacingPx }) => {
  // Profili come rettangoli ravvicinati (visti di taglio) distanziati col passo
  const profiles: number[] = [];
  if (spacingPx > 6) {
    const offset = spacingPx / 2;
    for (let cx = x + offset; cx < x + w; cx += spacingPx) {
      profiles.push(cx);
    }
  }
  const profileWidthPx = Math.max(8, Math.min(20, h * 0.7)); // sezione del profilo
  return (
    <g>
      {/* Background della fascia orditura (molto chiaro) */}
      <rect x={x} y={y} width={w} height={h} fill="#F1F5F9" stroke="#CBD5E1" strokeWidth={0.3} />
      {/* Profili di taglio (rettangoli con riempimento metal) */}
      {profiles.map((cx, i) => (
        <rect
          key={i}
          x={cx - profileWidthPx / 2}
          y={y + 1}
          width={profileWidthPx}
          height={h - 2}
          fill="url(#grad-metal)"
          stroke="#475569"
          strokeWidth={0.5}
          rx={1}
        />
      ))}
      {/* Etichetta livello orditura */}
      <text x={x + 6} y={y + h / 2 + 3} fontSize={8} fill="#475569" fontFamily="Inter, system-ui, sans-serif" letterSpacing="0.05em">
        {block.level === 'primary' ? 'ORDITURA PRIMARIA' : 'ORDITURA SECONDARIA'}
      </text>
    </g>
  );
};

const InsulationRect: React.FC<{ x: number; y: number; w: number; h: number; fillId: string }> = ({
  x, y, w, h, fillId,
}) => (
  <g>
    <rect x={x} y={y} width={w} height={h} fill={`url(#${fillId})`} stroke="#D4D4D8" strokeWidth={0.4} />
    <text x={x + 6} y={y + h / 2 + 3} fontSize={8} fill="#52525B" fontFamily="Inter, system-ui, sans-serif" letterSpacing="0.05em">
      ISOLANTE
    </text>
  </g>
);

const BoardRect: React.FC<{ x: number; y: number; w: number; h: number; fillId: string }> = ({
  x, y, w, h, fillId,
}) => (
  <g>
    <rect x={x} y={y} width={w} height={h} fill={`url(#${fillId})`} stroke="#71717A" strokeWidth={0.6} />
  </g>
);

const VerticalDimension: React.FC<{
  x: number; y1: number; y2: number; label: string; fontSize?: number; color?: string; strong?: boolean;
}> = ({ x, y1, y2, label, fontSize = 10, color = '#64748B', strong = false }) => {
  const len = Math.abs(y2 - y1);
  if (len < 8) return null;
  const tickW = 8;
  return (
    <g>
      <line x1={x} y1={y1} x2={x} y2={y2} stroke={color} strokeWidth={strong ? 0.8 : 0.4} />
      <line x1={x - tickW / 2} y1={y1} x2={x + tickW / 2} y2={y1} stroke={color} strokeWidth={strong ? 0.8 : 0.4} />
      <line x1={x - tickW / 2} y1={y2} x2={x + tickW / 2} y2={y2} stroke={color} strokeWidth={strong ? 0.8 : 0.4} />
      <text
        x={x - 6}
        y={(y1 + y2) / 2 + 3}
        fontSize={fontSize}
        fontWeight={strong ? 700 : 400}
        fill={color}
        textAnchor="end"
        fontFamily="Inter, system-ui, sans-serif"
      >
        {label}
      </text>
    </g>
  );
};

const Callout: React.FC<{ num: number; x: number; y: number }> = ({ num, x, y }) => (
  <g>
    <circle cx={x} cy={y} r={10} fill="#1E40AF" stroke="#ffffff" strokeWidth={1.5} />
    <text x={x} y={y + 3} fontSize={10} fontWeight={700} fill="#ffffff" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif">
      {num}
    </text>
  </g>
);

const CeilingSidebar: React.FC<{
  x: number; y: number; w: number; h: number;
  drawBlocks: { block: CeilingBlock; y: number; h: number; calloutNum: number }[];
  model: CeilingSectionModel;
  ceiling: CeilingDimensionalState;
}> = ({ x, y, w, h, drawBlocks, model, ceiling }) => {
  const PAD = 16;
  const innerX = x + PAD;
  const innerW = w - PAD * 2;
  let cursorY = y + PAD;

  const drawText = (text: string, opts: { fontSize?: number; fontWeight?: number; color?: string; uppercase?: boolean } = {}) => {
    const { fontSize = 10, fontWeight = 400, color = '#0F172A' } = opts;
    const el = (
      <text
        x={innerX}
        y={cursorY}
        fontSize={fontSize}
        fontWeight={fontWeight}
        fill={color}
        fontFamily="Inter, system-ui, sans-serif"
        letterSpacing={opts.uppercase ? '0.1em' : undefined}
      >
        {text}
      </text>
    );
    cursorY += fontSize + 4;
    return el;
  };

  const items: React.ReactNode[] = [];

  // Legenda
  items.push(<g key="legend-title">{drawText('COMPOSIZIONE', { fontSize: 9, color: '#94A3B8', uppercase: true })}</g>);
  cursorY += 4;
  drawBlocks
    .filter((db) => db.block.kind !== 'slab')
    .forEach((db) => {
      const yRow = cursorY;
      const blockLabel =
        db.block.kind === 'board' ? db.block.label :
        db.block.kind === 'hanger_space' ? 'Camera tecnica' :
        db.block.kind === 'frame' ? (db.block.level === 'primary' ? 'Orditura primaria' : 'Orditura secondaria') :
        db.block.kind === 'ceiling_insulation' ? db.block.label : '—';
      const depth =
        db.block.kind === 'board' ? db.block.thicknessMm :
        db.block.kind === 'hanger_space' ? db.block.depthMm :
        db.block.kind === 'frame' ? db.block.depthMm :
        db.block.kind === 'ceiling_insulation' ? db.block.depthMm : 0;

      items.push(
        <g key={`row-${db.calloutNum}`}>
          {/* Pallino numero */}
          <circle cx={innerX + 8} cy={yRow + 4} r={7} fill="#1E40AF" />
          <text x={innerX + 8} y={yRow + 7} fontSize={9} fontWeight={700} fill="#ffffff" textAnchor="middle" fontFamily="Inter, system-ui, sans-serif">
            {db.calloutNum}
          </text>
          {/* Label */}
          <text x={innerX + 22} y={yRow + 8} fontSize={10} fill="#0F172A" fontFamily="Inter, system-ui, sans-serif">
            {blockLabel.length > 38 ? blockLabel.slice(0, 36) + '…' : blockLabel}
          </text>
          {/* Spessore */}
          <text x={innerX + innerW} y={yRow + 8} fontSize={9} fill="#64748B" textAnchor="end" fontFamily="ui-monospace, monospace">
            {depth} mm
          </text>
        </g>,
      );
      cursorY += 18;
    });

  // Totale
  cursorY += 8;
  items.push(
    <g key="total">
      <line x1={innerX} y1={cursorY - 8} x2={innerX + innerW} y2={cursorY - 8} stroke="#E2E8F0" strokeWidth={0.5} />
      <text x={innerX} y={cursorY + 5} fontSize={10} fontWeight={600} fill="#0F172A" fontFamily="Inter, system-ui, sans-serif">
        Spessore totale
      </text>
      <text x={innerX + innerW} y={cursorY + 5} fontSize={11} fontWeight={700} fill="#0F172A" textAnchor="end" fontFamily="ui-monospace, monospace">
        {formatNumber(model.totalDepthMm, 0)} mm
      </text>
    </g>,
  );
  cursorY += 24;

  // Box configurazione tecnica
  items.push(<g key="config-title">{drawText('CONFIGURAZIONE', { fontSize: 9, color: '#94A3B8', uppercase: true })}</g>);
  cursorY += 4;
  const configRow = (label: string, value: string) => {
    const yRow = cursorY;
    cursorY += 16;
    return (
      <g key={label}>
        <text x={innerX} y={yRow + 8} fontSize={9} fill="#64748B" fontFamily="Inter, system-ui, sans-serif">{label}</text>
        <text x={innerX + innerW} y={yRow + 8} fontSize={10} fontWeight={500} fill="#0F172A" textAnchor="end" fontFamily="ui-monospace, monospace">
          {value}
        </text>
      </g>
    );
  };
  items.push(configRow('Attacco', model.isSuspended ? 'Pendinato' : 'A contatto'));
  items.push(configRow('Orditura', model.hasDoubleStructure ? 'Doppia' : 'Singola'));
  if (model.isSuspended && model.hangerCountPerSqm !== null) {
    items.push(configRow('Pendini/m²', formatNumber(model.hangerCountPerSqm, 2)));
    items.push(configRow('Passo pendini', `${ceiling.hangerSpacingPrimaryMm}×${ceiling.hangerSpacingPerpendicularMm} mm`));
  }
  items.push(configRow('Passo primaria', `${ceiling.primarySpacingMm} mm`));
  if (ceiling.secondarySpacingMm) {
    items.push(configRow('Passo secondaria', `${ceiling.secondarySpacingMm} mm`));
  }
  items.push(configRow('Pannelli/Lastre', String(model.boardCount)));

  return (
    <g>
      <rect x={x} y={y} width={w} height={h} fill="#FAFAFA" stroke="#E4E4E7" strokeWidth={0.5} />
      {items}
    </g>
  );
};

export default TechnicalCeilingDrawing;
